import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useLocation } from 'react-router-dom';
import { Spinner, theme } from '@kvdbil/components';
import { renderRoutes } from '~/config/routes/renderRoutes';
import { useTranslation } from '~/Locale';
import SiteMessage from '~/App/shared/components/SiteMessage';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import Header from './components/Header';
import Footer from './components/Footer';
import KundoAiChatButton from './components/KundoAiChatButton';
import ValuationButton from './components/ValuationButton';
import routes from '~/routes';
import WebsocketIndividualConnection from './shared/components/WebsocketIndividualConnection';
import UpdateViewingAuction from './components/UpdateViewingAuction';
import Snackbars from './components/Snackbars';
import { showFooter } from '~/helpers/footerHelpers';
import { showHeader } from '~/helpers/headerHelpers';
import AppLoaderOverlay from './shared/components/AppLoaderOverlay';
import { appIsLoading, messagesBottom } from './shared/selectors/global';
import { dismissMessage } from './shared/actions/messages';
import styled from 'styled-components';
import { ReduxDispatch } from './shared/interfaces/Redux';
import CanonicalHeadLink from './components/CanonicalHeadLink';
import { SiteMessageRecord } from '~/config/generated/graphql';
import LanguageModal from '~/App/shared/localization/components/LanguageModal';
import KvdHelper from './components/KvdHelper';
import { useFeatureFlag, useFeatureFlagValue } from './components/FeatureFlags';
import { useInitFirebase } from './firebase/useInitFirebase';
import ConsentBasedScripts from './shared/components/ConsentBasedScripts';
import { isOnServer } from '~/helpers/isOnServer';
import { ToTopButton } from './components/ToTopButton';
import { SiteMessageType } from './shared/interfaces/Message';
import useIsRouteAvailableOnDomain from './shared/hooks/useIsRouteAvailableOnDomain';

const Container = styled.div`
  position: fixed;
  z-index: 12;
  bottom: 0;
  left: 0;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background.gray};
`;

interface Props {
  appIsLoading: boolean;
  messagesBottom: SiteMessageRecord[];
  onMessageDismiss: (id: string, type: string) => void;
}
export const App = ({
  appIsLoading,
  messagesBottom,
  onMessageDismiss
}: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isFooter = showFooter(t, location.pathname);
  const isHeader = showHeader(t, location.pathname);
  const { value: showKvdHelper } = useFeatureFlag('kvd-helper');
  const isFloatingValuationCtaEnabled = useFeatureFlagValue(
    'floating-valuation-cta'
  );
  const compiledRoutes = routes(t);
  const isSellCarInfoAvailable = useIsRouteAvailableOnDomain(
    '/ROUTES.SELL/ROUTES.SELL_CAR_INFO'
  );

  useInitFirebase();

  return (
    <div>
      {appIsLoading && (
        <AppLoaderOverlay>
          <Spinner
            customColor={theme.colors.grayscaleToned.light4}
            size="large"
            speed="fast"
          />
        </AppLoaderOverlay>
      )}
      <UpdateViewingAuction />
      <CanonicalHeadLink pathname={location.pathname} />
      {isHeader && <Header />}
      <ContentContainer>{renderRoutes(compiledRoutes)}</ContentContainer>
      <ToTopButton />
      {isFloatingValuationCtaEnabled && isSellCarInfoAvailable && (
        <ValuationButton />
      )}
      <KundoAiChatButton />
      <WebsocketIndividualConnection />
      <Container>
        {messagesBottom &&
          messagesBottom.length > 0 &&
          messagesBottom.map((message, key) => (
            <SiteMessage
              key={key}
              type={(message.siteMessageType ?? '') as SiteMessageType}
              onDismiss={() =>
                onMessageDismiss(
                  message.id,
                  message.siteMessageType ?? 'Information'
                )
              }
            >
              <StructuredText
                data={message.messageStructuredText?.structuredText}
                options={{ paragraphElem: 'span' }}
              />
            </SiteMessage>
          ))}
      </Container>
      <Snackbars />
      {isFooter && <Footer />}

      {showKvdHelper && <KvdHelper location={location.pathname} />}

      <LanguageModal />
      {!isOnServer && <ConsentBasedScripts />}
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  appIsLoading: appIsLoading,
  messagesBottom: messagesBottom
});

const mapDispatchToProps = (dispatch: ReduxDispatch) => ({
  onMessageDismiss: (id: string, type: string) =>
    dispatch(dismissMessage(id, type))
});

const exportedApp = connect(mapStateToProps, mapDispatchToProps)(App);

export default exportedApp;
